import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import ApplicationForm from '../components/internships/ApplicationForm';
import KanbanBoard from '../components/internships/KanbanBoard';
import { internshipService } from '../services/api';
import SkeletonLoader from '../components/ui/SkeletonLoader';

const STATUSES = ['applied', 'interview', 'offer', 'rejected', 'withdrawn'];

const STATUS_COLORS = {
    applied: '#3b82f6',
    interview: '#f59e0b',
    offer: '#10b981',
    rejected: '#ef4444',
    withdrawn: '#6b7280',
};

function InternshipsPage() {
    const { t } = useTranslation();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedAppId, setSelectedAppId] = useState(null);
    const [activeTab, setActiveTab] = useState('applied');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

    useEffect(() => {
        fetchApplications();
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchApplications = async () => {
        try {
            const response = await internshipService.list();
            setApplications(response.data);
        } catch (err) {
            setError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (formData, resetForm) => {
        const cleanForm = {
            ...formData,
            company_name: formData.company_name.trim(),
            position: formData.position.trim(),
            job_url: formData.job_url.trim(),
            salary_range: formData.salary_range.trim(),
            job_description: formData.job_description.trim(),
            applied_date: formData.applied_date || null,
            deadline: formData.deadline || null,
        };
        if (!cleanForm.company_name || !cleanForm.position) {
            setError(t('common.error'));
            return;
        }
        try {
            await internshipService.create(cleanForm);
            setShowForm(false);
            setError('');
            resetForm();
            fetchApplications();
        } catch (err) {
            setError(t('common.error'));
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await internshipService.update(id, { status: newStatus });
            setApplications(applications.map(app =>
                app.id === id ? { ...app, status: newStatus } : app
            ));
        } catch (err) {
            setError(t('common.error'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('common.delete_confirm'))) return;
        try {
            await internshipService.delete(id);
            setApplications(applications.filter(app => app.id !== id));
            if (selectedAppId === id) setSelectedAppId(null);
        } catch (err) {
            setError(t('common.error'));
        }
    };

    const handleAddNote = async (appId, noteText) => {
        try {
            await internshipService.addNote(appId, { note_text: noteText });
            fetchApplications();
        } catch (err) {
            setError(t('common.error'));
        }
    };

    const handleSelect = (id) => {
        setSelectedAppId(selectedAppId === id ? null : id);
    };

    // translated status labels
    const statusLabel = (status) => {
        const map = {
            applied: t('internships.applied'),
            interview: t('internships.interview'),
            offer: t('internships.offer'),
            rejected: t('internships.rejected'),
            withdrawn: t('internships.withdrawn'),
        };
        return map[status] || status;
    };

    if (loading) return (
    <Layout>
        <div className="page-container"><SkeletonLoader /></div>
    </Layout>
    );

    const MobileView = () => {
        const filtered = applications.filter(a => a.status === activeTab);
        return (
            <div className="kanban-mobile">
                <div className="kanban-tabs">
                    {STATUSES.map(status => {
                        const count = applications.filter(a => a.status === status).length;
                        return (
                            <button
                                key={status}
                                onClick={() => setActiveTab(status)}
                                className={`kanban-tab ${activeTab === status ? 'active' : ''}`}
                                style={{
                                    borderBottomColor: activeTab === status ? STATUS_COLORS[status] : 'transparent',
                                    color: activeTab === status ? STATUS_COLORS[status] : '#64748b',
                                }}
                            >
                                {statusLabel(status)}
                                {count > 0 && (
                                    <span
                                        className="kanban-tab-count"
                                        style={{ background: STATUS_COLORS[status] }}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="kanban-mobile-cards">
                    {filtered.length === 0 ? (
                        <div className="empty-state">
                            {t('internships.no_applications')}
                        </div>
                    ) : (
                        filtered.map(app => (
                            <div
                                key={app.id}
                                className="kanban-card"
                                onClick={() => handleSelect(app.id)}
                                style={{ marginBottom: '0.75rem' }}
                            >
                                <strong>{app.position}</strong>
                                <p>{app.company_name}</p>
                                {app.applied_date && (
                                    <small>{app.applied_date}</small>
                                )}

                                {selectedAppId === app.id && (
                                    <div className="card-expanded">
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <label style={{
                                                fontSize: '0.8rem',
                                                color: '#64748b',
                                                display: 'block',
                                                marginBottom: '0.4rem'
                                            }}>
                                                {t('internships.change_status')}
                                            </label>
                                            <select
                                                value={app.status}
                                                onChange={e => handleStatusChange(app.id, e.target.value)}
                                                onClick={e => e.stopPropagation()}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.4rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {STATUSES.map(s => (
                                                    <option key={s} value={s}>{statusLabel(s)}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {app.notes?.length > 0 && (
                                            <div className="notes-section">
                                                {app.notes.map((note, i) => (
                                                    <div key={i} className="note-item">{note.note_text}</div>
                                                ))}
                                            </div>
                                        )}

                                        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                                            <input
                                                type="text"
                                                placeholder={t('internships.add_note')}
                                                id={`note-${app.id}`}
                                                onClick={e => e.stopPropagation()}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.4rem 0.6rem',
                                                    borderRadius: '6px',
                                                    border: '1px solid #d1d5db',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                            <button
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    const input = document.getElementById(`note-${app.id}`);
                                                    if (input.value.trim()) {
                                                        handleAddNote(app.id, input.value.trim());
                                                        input.value = '';
                                                    }
                                                }}
                                                className="btn-primary"
                                                style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                                            >
                                                {t('internships.add')}
                                            </button>
                                        </div>

                                        <button
                                            onClick={e => { e.stopPropagation(); handleDelete(app.id); }}
                                            className="btn-danger"
                                            style={{ marginTop: '0.75rem', width: '100%' }}
                                        >
                                            {t('internships.delete')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="page-container">
                <div className="page-header">
                    <div>
                        <h1>{t('internships.title')}</h1>
                        <p className="subtitle">{t('internships.subtitle')}</p>
                    </div>
                    <button onClick={() => setShowForm(!showForm)} className="btn-primary">
                        {showForm ? t('internships.cancel') : t('internships.add_application')}
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                {showForm && (
                    <ApplicationForm onSubmit={handleSubmit} error={error} />
                )}

                {isMobile ? (
                    <MobileView />
                ) : (
                    <KanbanBoard
                        applications={applications}
                        selectedAppId={selectedAppId}
                        onSelect={handleSelect}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                        onAddNote={handleAddNote}
                    />
                )}
            </div>
        </Layout>
    );
}

export default InternshipsPage;